import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  QuickFillOption,
  QuickFillProperty,
  QuickFillItem,
  QuickFillFilters,
} from '../types';
import {
  type AutoFillResult,
  type QuickFillAutoFillMode,
} from '../lib/utils/autoFillService';
import {
  AutoFillLogSampleSize,
  computeAutoFillRows,
  getCellProtection,
  sortUniqueRowIndices,
  summarizeAutoFillStoreRow,
} from '../lib/quickFill/autoFillRowComputation';
import { webLog } from '../api/grpc/webLogger';
import { getStorage } from '../lib/utils/safeStorage';
import { normalizeRevitParameterName } from '../lib/revitParameterNames';

// Re-export types for backward compatibility
export type { QuickFillOption, QuickFillProperty, QuickFillItem, QuickFillFilters };

export interface QuickFillRollbackSnapshot {
  id: string;
  label: string;
  createdAt: string;
  properties: QuickFillProperty[];
  extraProperties: QuickFillProperty[];
  quickFillData: QuickFillItem[];
  selectedItems: string[];
  filters: QuickFillFilters;
}

export interface QuickFillSessionUndoRowIdentity {
  name: string;
  rowMode: 'familyType' | 'instance';
  elementId: string;
  familyTypeKey: string;
}

export interface QuickFillSessionUndoSnapshot {
  id: string;
  sessionId: string;
  label: string;
  createdAt: string;
  rowIdentity: QuickFillSessionUndoRowIdentity[];
  properties: QuickFillProperty[];
  extraProperties: QuickFillProperty[];
  quickFillData: QuickFillItem[];
}

// ============================================================================
// State Interface
// ============================================================================

interface QuickFillState {
  // Properties configuration
  properties: QuickFillProperty[];

  // Extra/dynamic properties added by user
  extraProperties: QuickFillProperty[];

  // Quick fill data items
  quickFillData: QuickFillItem[];
  baselineQuickFillData: QuickFillItem[];
  editedCells: Record<string, boolean>;
  hasPendingChanges: boolean;

  // Active filters
  filters: QuickFillFilters;

  // Loading states
  isLoading: boolean;
  isRevitDataLoaded: boolean;
  error: string | null;

  // Selection state
  selectedItems: string[];

  // Local rollback snapshots for quick-fix undo flows
  rollbackSnapshots: QuickFillRollbackSnapshot[];

  // Cross-page-visit undo snapshots for Quick Fill value edits
  sessionUndoSnapshots: QuickFillSessionUndoSnapshot[];
}

// ============================================================================
// Actions Interface
// ============================================================================

interface QuickFillActions {
  // Property actions
  updatePropertyValue: (id: string, value: string) => void;
  updatePropertyRevitParameter: (id: string, revitParameter: string) => void;
  updatePropertyChecked: (id: string, checked: boolean) => void;
  togglePropertyChecked: (id: string) => void;
  setProperties: (properties: QuickFillProperty[]) => void;
  initializeProperties: () => void;
  replaceProfileConfiguration: (config: {
    properties?: QuickFillProperty[];
    extraProperties?: QuickFillProperty[];
  }) => void;

  // Extra properties actions
  addExtraProperty: (property: QuickFillProperty) => void;
  upsertExtraProperties: (properties: QuickFillProperty[]) => void;
  removeExtraProperty: (id: string) => void;
  updateExtraPropertyChecked: (id: string, checked: boolean) => void;

  // Quick fill data actions
  replaceQuickFillDataFromSource: (data: QuickFillItem[]) => void;
  commitQuickFillDataAsBaseline: () => void;
  setQuickFillData: (data: QuickFillItem[]) => void;
  addQuickFillItem: (item: QuickFillItem) => void;
  updateQuickFillItem: (index: number, item: Partial<QuickFillItem>) => void;
  removeQuickFillItem: (index: number) => void;

  // Filter actions
  updateFilter: (field: string, value: string) => void;
  resetFilters: () => void;
  getFilteredData: () => QuickFillItem[];

  // Selection actions
  selectItem: (name: string) => void;
  deselectItem: (name: string) => void;
  toggleItemSelection: (name: string) => void;
  selectAll: () => void;
  deselectAll: () => void;

  // Loading state actions
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setRevitDataLoaded: (isLoaded: boolean) => void;

  // Rollback snapshot actions
  captureRollbackSnapshot: (label: string) => string;
  restoreRollbackSnapshot: (snapshotId: string) => boolean;
  discardRollbackSnapshot: (snapshotId: string) => void;
  captureSessionUndoSnapshot: (label: string, sessionId: string) => string | null;
  restoreSessionUndoSnapshot: (snapshotId: string) => boolean;
  clearSessionUndoSnapshots: (snapshotIds: string[]) => void;

  // Apply actions
  applyQuickFill: (itemNames: string[]) => Promise<void>;

  // Auto-fill actions
  autoFillRows: (indices: number[]) => { filled: number; total: number; results: Map<number, AutoFillResult> };
  autoFillRowsWithLabels: (
    indices: number[],
    labels: string[],
    mode?: QuickFillAutoFillMode
  ) => { filled: number; total: number; results: Map<number, AutoFillResult> };
  autoFillAllRows: () => { filled: number; total: number; results: Map<number, AutoFillResult> };
  autoFillSelectedRows: () => { filled: number; total: number; results: Map<number, AutoFillResult> };

  // Reset
  reset: () => void;
}

// ============================================================================
// Default Properties
// ============================================================================

export const createDefaultQuickFillProperties = (): QuickFillProperty[] => [
  {
    id: 'skey',
    label: 'SKEY',
    value: '',
    revitParameter: '#PCF_SKEY',  // Updated to match actual Revit parameter name
    options: [], // Options come from shared parameters/workflow
    checked: true,
  },
  {
    id: 'object-type',
    label: 'OBJECT-TYPE',
    value: '',
    revitParameter: '#PCF_OBJECT_TYPE',  // Updated to match actual Revit parameter name
    options: [], // Options come from shared parameters/workflow
    checked: true,
  },
  {
    id: 'end1',
    label: 'End1',
    value: '',
    revitParameter: 'PiX_PPG_END1_TYPE',
    options: [], // Options come from shared parameters/workflow
    checked: true,
  },
  {
    id: 'end2',
    label: 'End2',
    value: '',
    revitParameter: 'PiX_PPG_END2_TYPE',
    options: [], // Options come from shared parameters/workflow
    checked: true,
  },
  {
    id: 'end3',
    label: 'End3',
    value: '',
    revitParameter: 'PiX_PPG_END3_TYPE',
    options: [], // Options come from shared parameters/workflow
    checked: true,
  },
  {
    id: 'end4',
    label: 'End4',
    value: '',
    revitParameter: 'PiX_PPG_END4_TYPE',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'piping-spec',
    label: 'PIPING-SPEC',
    value: '',
    revitParameter: 'PiX_PPG_Spec',  // Fixed: was PiP_PPG_SPEC
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  // NOTE: SYSTEM-GROUPING is a variable (pipe system name), not a parameter
  // It should not be included in QuickFill as it's not a family parameter
  {
    id: 'material',
    label: 'MATERIAL',
    value: '',
    revitParameter: 'PiX_03_Material_ENG',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'design-std',
    label: 'DESIGN-STD',
    value: '',
    revitParameter: 'PiX_02_Standard_ENG',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'pressure-class',
    label: 'PRESSURE-CLASS',
    value: '',
    revitParameter: 'PiX_PPG_END1_PressureRating',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'insulation-spec',
    label: 'INSULATION-SPEC',
    value: '',
    revitParameter: 'PiX_GN_2_InsulationSpec',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'insulation-thickness',
    label: 'INSULATION-THICKNESS',
    value: '',
    revitParameter: 'PiX_GN_2_InsulationThickness',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'tracing-spec',
    label: 'TRACING-SPEC',
    value: '',
    revitParameter: 'PiX_GN_2_HeatTracingSpec',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
  {
    id: 'item-description',
    label: 'ITEM-DESCRIPTION',
    value: '',
    revitParameter: 'PiX_01_Description_ENG',
    options: [], // Options come from shared parameters/workflow
    checked: false,
  },
];


// ============================================================================
// Initial State
// ============================================================================

const initialState: QuickFillState = {
  properties: [],
  extraProperties: [],
  quickFillData: [],
  baselineQuickFillData: [],
  editedCells: {},
  hasPendingChanges: false,
  filters: { name: '' },
  isLoading: false,
  isRevitDataLoaded: false,
  error: null,
  selectedItems: [],
  rollbackSnapshots: [],
  sessionUndoSnapshots: [],
};

const SYSTEM_GROUPING_TOKENS = new Set(['BySystemName', 'BySystemTypeName']);

const shouldExcludeQuickFillProperty = (prop: any): boolean => {
  if (!prop) return true;
  if (prop.label === 'SYSTEM-GROUPING') return true;
  if (SYSTEM_GROUPING_TOKENS.has(prop.revitParameter)) return true;
  if (SYSTEM_GROUPING_TOKENS.has(prop.value)) return true;
  return false;
};

const sanitizeQuickFillProperty = (prop: any): any => {
  if (!prop) return null;
  const normalizedIsInstance =
    typeof prop.isInstance === 'boolean'
      ? prop.isInstance
      : undefined;
  return {
    ...prop,
    revitParameter: prop.revitParameter ? normalizeRevitParameterName(prop.revitParameter) : prop.revitParameter,
    ...(normalizedIsInstance !== undefined ? { isInstance: normalizedIsInstance } : {}),
  };
};

const normalizeExtraPropertyId = (
  id: string | undefined,
  revitParameter?: string
): string => {
  const rawId = typeof id === 'string' ? id.trim() : '';
  if (rawId.startsWith('extra-')) {
    return rawId;
  }

  const fallbackSource = (typeof revitParameter === 'string' && revitParameter.trim() !== '')
    ? revitParameter
    : rawId || 'column';

  const slug = fallbackSource.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `extra-${slug || 'column'}`;
};

const normalizeExtraPropertyMatchValue = (value: string | undefined | null): string => {
  if (!value) return '';
  return normalizeRevitParameterName(value).trim().toLowerCase();
};

const isSameExtraProperty = (
  left: QuickFillProperty | null | undefined,
  right: QuickFillProperty | null | undefined
): boolean => {
  if (!left || !right) return false;
  if (left.id && right.id && left.id.trim().toLowerCase() === right.id.trim().toLowerCase()) {
    return true;
  }
  const leftParam = normalizeExtraPropertyMatchValue(left.revitParameter);
  const rightParam = normalizeExtraPropertyMatchValue(right.revitParameter);
  if (leftParam && leftParam === rightParam) {
    return true;
  }
  const leftLabel = (left.label ?? '').trim().toLowerCase();
  const rightLabel = (right.label ?? '').trim().toLowerCase();
  if (leftLabel && leftLabel === rightLabel) {
    return true;
  }
  return false;
};

const AutoFillLogSource = 'quickFillAutoFill';
const QuickFillAttentionLogSource = 'quickFillAttention';
const stringifyAutoFillStoreLog = (event: string, payload: Record<string, unknown>): string => {
  try {
    return `${event} ${JSON.stringify(payload)}`;
  } catch {
    return `${event} <unserializable>`;
  }
};

const logQuickFillAttention = (event: string, payload: Record<string, unknown>): void => {
  webLog.info(QuickFillAttentionLogSource, stringifyAutoFillStoreLog(event, payload));
};

const valuesEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }

  if (
    left !== null &&
    right !== null &&
    typeof left === 'object' &&
    typeof right === 'object'
  ) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }

  return false;
};

const sanitizeItemPatch = (
  existing: QuickFillItem | undefined,
  patch: Partial<QuickFillItem>
): Partial<QuickFillItem> => {
  const sanitized: Partial<QuickFillItem> = {};
  Object.entries(patch as Record<string, unknown>).forEach(([field, value]) => {
    if (field === 'Name') return;
    const protection = getCellProtection(existing, field);
    // Keep local Quick Fill edits available even when the current family/type
    // does not expose the mapped Revit parameter yet. Revit save still skips
    // unsupported cells later via __hasParamFlags.
    if (protection.readOnly) return;
    if (valuesEqual((existing as Record<string, unknown> | undefined)?.[field], value)) {
      return;
    }
    (sanitized as Record<string, unknown>)[field] = value;
  });
  return sanitized;
};

const cloneQuickFillData = (data: QuickFillItem[]): QuickFillItem[] =>
  data.map((item) => ({ ...item }));

const cloneQuickFillProperties = (properties: QuickFillProperty[]): QuickFillProperty[] =>
  properties.map((property) => ({ ...property }));

const quickFillRowsEqual = (left: QuickFillItem[], right: QuickFillItem[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((row, index) => valuesEqual(row, right[index]));
};

const getSessionUndoRowIdentity = (item: QuickFillItem | undefined): QuickFillSessionUndoRowIdentity => ({
  name: typeof item?.Name === 'string' ? item.Name : '',
  rowMode: (item as any)?.__rowMode === 'instance' ? 'instance' : 'familyType',
  elementId: typeof (item as any)?.__elementId === 'string' ? (item as any).__elementId : '',
  familyTypeKey:
    typeof (item as any)?.__familyTypeKey === 'string' && (item as any).__familyTypeKey.trim() !== ''
      ? (item as any).__familyTypeKey.trim()
      : (typeof item?.Name === 'string' ? item.Name : ''),
});

const buildSessionUndoRowIdentity = (data: QuickFillItem[]): QuickFillSessionUndoRowIdentity[] =>
  data.map(getSessionUndoRowIdentity);

export const isQuickFillSessionUndoSnapshotCompatible = (
  snapshot: QuickFillSessionUndoSnapshot | undefined | null,
  currentData: QuickFillItem[]
): boolean => {
  if (!snapshot || !Array.isArray(currentData)) {
    return false;
  }

  const rowIdentity = Array.isArray(snapshot.rowIdentity) && snapshot.rowIdentity.length > 0
    ? snapshot.rowIdentity
    : buildSessionUndoRowIdentity(Array.isArray(snapshot.quickFillData) ? snapshot.quickFillData : []);

  if (rowIdentity.length !== currentData.length) {
    return false;
  }

  return rowIdentity.every((identity, index) => {
    const currentIdentity = getSessionUndoRowIdentity(currentData[index]);
    return (
      identity.name === currentIdentity.name &&
      identity.rowMode === currentIdentity.rowMode &&
      identity.elementId === currentIdentity.elementId &&
      identity.familyTypeKey === currentIdentity.familyTypeKey
    );
  });
};

const hasSameQuickFillStructure = (
  baseline: QuickFillItem[],
  current: QuickFillItem[]
): boolean => {
  if (baseline.length !== current.length) {
    return false;
  }

  for (let index = 0; index < baseline.length; index++) {
    if ((baseline[index]?.Name ?? '') !== (current[index]?.Name ?? '')) {
      return false;
    }
  }

  return true;
};

const buildEditedCellsFromBaseline = (
  baseline: QuickFillItem[],
  current: QuickFillItem[]
): Record<string, boolean> => {
  if (
    baseline.length === 0 ||
    current.length === 0 ||
    !hasSameQuickFillStructure(baseline, current)
  ) {
    return {};
  }

  const edited: Record<string, boolean> = {};

  for (let rowIndex = 0; rowIndex < current.length; rowIndex++) {
    const baselineRow = baseline[rowIndex];
    const currentRow = current[rowIndex];
    if (!baselineRow || !currentRow) {
      continue;
    }

    const keys = new Set<string>([
      ...Object.keys(baselineRow as Record<string, unknown>),
      ...Object.keys(currentRow as Record<string, unknown>),
    ]);

    keys.forEach((key) => {
      if (key === 'Name' || key.startsWith('__')) {
        return;
      }

      const baselineValue = (baselineRow as Record<string, unknown>)[key];
      const currentValue = (currentRow as Record<string, unknown>)[key];
      if (!valuesEqual(baselineValue, currentValue)) {
        edited[`${rowIndex}-${key}`] = true;
      }
    });
  }

  return edited;
};

const hasEditedCells = (editedCells: Record<string, boolean>): boolean =>
  Object.values(editedCells).some(Boolean);

const getEditedCellCount = (editedCells: Record<string, boolean>): number =>
  Object.values(editedCells).filter(Boolean).length;

const resolveHasPendingQuickFillChanges = (
  baseline: QuickFillItem[],
  current: QuickFillItem[],
  editedCells: Record<string, boolean>,
  fallbackChanged: boolean
): boolean => {
  if (hasEditedCells(editedCells)) {
    return true;
  }

  return fallbackChanged && !hasSameQuickFillStructure(baseline, current);
};

// ============================================================================
// Store Creation
// ============================================================================

export const useQuickFillStore = create<QuickFillState & QuickFillActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Property actions
      updatePropertyValue: (id, value) => {
        set((state) => {
          const props = Array.isArray(state.properties) ? state.properties : [];
          return {
            properties: props.map((prop) =>
              prop && prop.id === id ? { ...prop, value } : prop
            ),
          };
        });
      },

      updatePropertyRevitParameter: (id, revitParameter) => {
        set((state) => {
          const props = Array.isArray(state.properties) ? state.properties : [];
          return {
            properties: props.map((prop) =>
              prop && prop.id === id ? { ...prop, revitParameter } : prop
            ),
          };
        });
      },

      updatePropertyChecked: (id, checked) => {
        set((state) => {
          const props = Array.isArray(state.properties) ? state.properties : [];
          const newProps = props.map((prop) =>
            prop && prop.id === id ? { ...prop, checked } : prop
          );

          return {
            properties: newProps,
          };
        });
      },

      togglePropertyChecked: (id) => {
        set((state) => {
          const props = Array.isArray(state.properties) ? state.properties : [];
          return {
            properties: props.map((prop) =>
              prop && prop.id === id ? { ...prop, checked: !prop.checked } : prop
            ),
          };
        });
      },

      setProperties: (properties) => {
        set({ properties });
      },

      initializeProperties: () => {
        const { properties, extraProperties } = get();

        // Safely check if properties array is empty or not an array
        const propsArray = Array.isArray(properties) ? properties : [];
        const cleanedProps = propsArray
          .filter((prop) => !shouldExcludeQuickFillProperty(prop))
          .map(sanitizeQuickFillProperty)
          .filter(Boolean);
        const extrasArray = Array.isArray(extraProperties) ? extraProperties : [];
        const cleanedExtras = extrasArray
          .filter((prop) => !shouldExcludeQuickFillProperty(prop))
          .map((prop) => sanitizeQuickFillProperty({
            ...prop,
            id: normalizeExtraPropertyId(prop?.id, prop?.revitParameter),
          }))
          .filter(Boolean);

        // Only initialize default properties if none exist
        // Don't initialize quickFillData - it comes from Revit data
        if (cleanedProps.length === 0) {
          set({
            properties: createDefaultQuickFillProperties(),
            extraProperties: cleanedExtras,
          });
          return;
        }

        const propertiesChanged = cleanedProps.length !== propsArray.length
          || cleanedProps.some((prop, index) => prop !== propsArray[index]);
        const extrasChanged = cleanedExtras.length !== extrasArray.length
          || cleanedExtras.some((prop, index) => prop !== extrasArray[index]);

        if (propertiesChanged || extrasChanged) {
          set({
            ...(propertiesChanged ? { properties: cleanedProps } : {}),
            ...(extrasChanged ? { extraProperties: cleanedExtras } : {}),
          });
        }
      },

      replaceProfileConfiguration: ({ properties, extraProperties }) => {
        const defaultProperties = createDefaultQuickFillProperties();
        const normalizedProperties = (Array.isArray(properties) ? properties : defaultProperties)
          .filter((prop) => !shouldExcludeQuickFillProperty(prop))
          .map((prop) => sanitizeQuickFillProperty({
            ...prop,
            value: typeof prop?.value === 'string' ? prop.value : '',
            checked: prop?.checked !== false,
            options: [],
          }))
          .filter(Boolean) as QuickFillProperty[];

        const normalizedExtraProperties = (Array.isArray(extraProperties) ? extraProperties : [])
          .filter((prop) => !shouldExcludeQuickFillProperty(prop))
          .map((prop) => sanitizeQuickFillProperty({
            ...prop,
            id: normalizeExtraPropertyId(prop?.id, prop?.revitParameter),
            value: typeof prop?.value === 'string' ? prop.value : '',
            checked: prop?.checked !== false,
            options: [],
          }))
          .filter(Boolean) as QuickFillProperty[];

        const dedupedExtraProperties = normalizedExtraProperties.reduce<QuickFillProperty[]>(
          (accumulated, property) => {
            if (accumulated.some((existing) => isSameExtraProperty(existing, property))) {
              return accumulated;
            }
            accumulated.push(property);
            return accumulated;
          },
          []
        );

        set({
          properties: normalizedProperties.length > 0
            ? normalizedProperties
            : defaultProperties,
          extraProperties: dedupedExtraProperties,
        });
      },

      // Extra properties actions
      addExtraProperty: (property) => {
        set((state) => {
          const extras = Array.isArray(state.extraProperties) ? state.extraProperties : [];
          const normalizedProperty = sanitizeQuickFillProperty({
            ...property,
            id: normalizeExtraPropertyId(property?.id, property?.revitParameter),
          }) as QuickFillProperty;
          // Check if property already exists
          if (
            extras.some((candidate) =>
              candidate && isSameExtraProperty(candidate, normalizedProperty)
            )
          ) {
            return state;
          }
          return {
            extraProperties: [...extras, normalizedProperty],
          };
        });
      },

      upsertExtraProperties: (properties) => {
        set((state) => {
          const extras = Array.isArray(state.extraProperties) ? state.extraProperties : [];
          const normalizedIncoming = properties
            .map((property) => sanitizeQuickFillProperty({
              ...property,
              id: normalizeExtraPropertyId(property?.id, property?.revitParameter),
            }) as QuickFillProperty | null)
            .filter((property): property is QuickFillProperty => !!property);

          if (normalizedIncoming.length === 0) {
            return state;
          }

          const nextExtras = [...extras];
          let changed = false;

          normalizedIncoming.forEach((property) => {
            const existingIndex = nextExtras.findIndex((candidate) =>
              candidate && isSameExtraProperty(candidate, property)
            );

            if (existingIndex >= 0) {
              const existing = nextExtras[existingIndex];
              const updated = {
                ...existing,
                ...property,
              };
              if (JSON.stringify(existing) !== JSON.stringify(updated)) {
                nextExtras[existingIndex] = updated;
                changed = true;
              }
              return;
            }

            nextExtras.push(property);
            changed = true;
          });

          return changed ? { extraProperties: nextExtras } : state;
        });
      },

      removeExtraProperty: (id) => {
        set((state) => {
          const extras = Array.isArray(state.extraProperties) ? state.extraProperties : [];
          return {
            extraProperties: extras.filter(p => p && p.id !== id),
          };
        });
      },

      updateExtraPropertyChecked: (id, checked) => {
        set((state) => {
          const extras = Array.isArray(state.extraProperties) ? state.extraProperties : [];
          return {
            extraProperties: extras.map((prop) =>
              prop && prop.id === id ? { ...prop, checked } : prop
            ),
          };
        });
      },

      // Quick fill data actions
      replaceQuickFillDataFromSource: (data) => {
        const nextData = Array.isArray(data) ? cloneQuickFillData(data) : [];
        set((state) => {
          if (!state.hasPendingChanges) {
            return {
              quickFillData: nextData,
              baselineQuickFillData: cloneQuickFillData(nextData),
              editedCells: {},
              hasPendingChanges: false,
            };
          }

          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, nextData);
          const fallbackChanged = !quickFillRowsEqual(state.baselineQuickFillData, nextData);
          const hasPendingChanges = resolveHasPendingQuickFillChanges(
            state.baselineQuickFillData,
            nextData,
            editedCells,
            fallbackChanged
          );
          logQuickFillAttention('source-replace-while-pending', {
            incomingRows: nextData.length,
            baselineRows: state.baselineQuickFillData.length,
            editedCellCount: getEditedCellCount(editedCells),
            fallbackChanged,
            resolvedPending: hasPendingChanges,
            sampleEditedKeys: Object.keys(editedCells).slice(0, 8),
          });
          return {
            quickFillData: nextData,
            editedCells,
            hasPendingChanges,
          };
        });
      },

      commitQuickFillDataAsBaseline: () => {
        set((state) => {
          const nextData = Array.isArray(state.quickFillData) ? cloneQuickFillData(state.quickFillData) : [];
          logQuickFillAttention('commit-baseline', {
            rows: nextData.length,
            previousEditedCellCount: getEditedCellCount(state.editedCells),
            previousPending: state.hasPendingChanges,
          });
          return {
            quickFillData: nextData,
            baselineQuickFillData: cloneQuickFillData(nextData),
            editedCells: {},
            hasPendingChanges: false,
          };
        });
      },

      setQuickFillData: (data) => {
        set((state) => {
          const previousData = Array.isArray(state.quickFillData) ? state.quickFillData : [];
          const nextData = Array.isArray(data) ? cloneQuickFillData(data) : [];
          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, nextData);
          const dataChanged = !quickFillRowsEqual(previousData, nextData);
          return {
            quickFillData: nextData,
            editedCells,
            hasPendingChanges: resolveHasPendingQuickFillChanges(
              state.baselineQuickFillData,
              nextData,
              editedCells,
              dataChanged
            ),
          };
        });
      },

      addQuickFillItem: (item) => {
        set((state) => {
          const data = Array.isArray(state.quickFillData) ? state.quickFillData : [];
          const nextData = [...data, item];
          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, nextData);
          return {
            quickFillData: nextData,
            editedCells,
            hasPendingChanges: resolveHasPendingQuickFillChanges(
              state.baselineQuickFillData,
              nextData,
              editedCells,
              true
            ),
          };
        });
      },

      updateQuickFillItem: (index, item) => {
        set((state) => {
          const data = Array.isArray(state.quickFillData) ? state.quickFillData : [];
          const existing = data[index];
          const sanitizedPatch = sanitizeItemPatch(existing, item);
          if (Object.keys(sanitizedPatch).length === 0) {
            return { quickFillData: data };
          }
          const nextData = data.map((existing, i) =>
            i === index ? { ...existing, ...sanitizedPatch } : existing
          );
          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, nextData);
          return {
            quickFillData: nextData,
            editedCells,
            hasPendingChanges: resolveHasPendingQuickFillChanges(
              state.baselineQuickFillData,
              nextData,
              editedCells,
              true
            ),
          };
        });
      },

      removeQuickFillItem: (index) => {
        set((state) => {
          const data = Array.isArray(state.quickFillData) ? state.quickFillData : [];
          const selected = Array.isArray(state.selectedItems) ? state.selectedItems : [];
          const nextData = data.filter((_, i) => i !== index);
          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, nextData);
          return {
            quickFillData: nextData,
            selectedItems: selected.filter(
              (name) => name !== data[index]?.Name
            ),
            editedCells,
            hasPendingChanges: resolveHasPendingQuickFillChanges(
              state.baselineQuickFillData,
              nextData,
              editedCells,
              true
            ),
          };
        });
      },

      // Filter actions
      updateFilter: (field, value) => {
        set((state) => ({
          filters: { ...state.filters, [field]: value },
        }));
      },

      resetFilters: () => {
        set((state) => ({
          filters: Object.keys(state.filters).reduce(
            (acc, key) => ({ ...acc, [key]: '' }),
            {} as QuickFillFilters
          ),
        }));
      },

      getFilteredData: () => {
        const { quickFillData, filters } = get();
        const data = Array.isArray(quickFillData) ? quickFillData : [];

        if (!filters || Object.values(filters).every((v) => !v)) {
          return data;
        }

        return data.filter((item) => {
          if (!item) return false;
          return Object.entries(filters).every(([field, value]) => {
            if (!value) return true;
            const itemValue = item[field as keyof QuickFillItem];
            return itemValue
              ? String(itemValue).toLowerCase().includes(value.toLowerCase())
              : false;
          });
        });
      },

      // Selection actions
      selectItem: (name) => {
        set((state) => {
          const selected = Array.isArray(state.selectedItems) ? state.selectedItems : [];
          return {
            selectedItems: selected.includes(name)
              ? selected
              : [...selected, name],
          };
        });
      },

      deselectItem: (name) => {
        set((state) => {
          const selected = Array.isArray(state.selectedItems) ? state.selectedItems : [];
          return {
            selectedItems: selected.filter((n) => n !== name),
          };
        });
      },

      toggleItemSelection: (name) => {
        set((state) => {
          const selected = Array.isArray(state.selectedItems) ? state.selectedItems : [];
          return {
            selectedItems: selected.includes(name)
              ? selected.filter((n) => n !== name)
              : [...selected, name],
          };
        });
      },

      selectAll: () => {
        const filteredData = get().getFilteredData();
        const data = Array.isArray(filteredData) ? filteredData : [];
        set({
          selectedItems: data.map((item) => item?.Name).filter(Boolean),
        });
      },

      deselectAll: () => {
        set({ selectedItems: [] });
      },

      // Loading state actions
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      setRevitDataLoaded: (isLoaded) => {
        set({ isRevitDataLoaded: isLoaded });
      },

      captureRollbackSnapshot: (label) => {
        const state = get();
        const snapshotId = `quickfill-snapshot-${Date.now()}-${Math.round(Math.random() * 1000)}`;
        const snapshot: QuickFillRollbackSnapshot = {
          id: snapshotId,
          label: label.trim() || 'Quick Fill rollback snapshot',
          createdAt: new Date().toISOString(),
          properties: JSON.parse(JSON.stringify(Array.isArray(state.properties) ? state.properties : [])),
          extraProperties: JSON.parse(JSON.stringify(Array.isArray(state.extraProperties) ? state.extraProperties : [])),
          quickFillData: JSON.parse(JSON.stringify(Array.isArray(state.quickFillData) ? state.quickFillData : [])),
          selectedItems: [...(Array.isArray(state.selectedItems) ? state.selectedItems : [])],
          filters: { ...(state.filters || { name: '' }) },
        };

        set((current) => ({
          rollbackSnapshots: [snapshot, ...current.rollbackSnapshots].slice(0, 12),
        }));

        return snapshotId;
      },

      restoreRollbackSnapshot: (snapshotId) => {
        const snapshot = get().rollbackSnapshots.find((candidate) => candidate.id === snapshotId);
        if (!snapshot) {
          return false;
        }

        const restoredData = JSON.parse(JSON.stringify(snapshot.quickFillData));
        set((state) => ({
          properties: JSON.parse(JSON.stringify(snapshot.properties)),
          extraProperties: JSON.parse(JSON.stringify(snapshot.extraProperties)),
          quickFillData: restoredData,
          selectedItems: [...snapshot.selectedItems],
          filters: { ...snapshot.filters },
          rollbackSnapshots: state.rollbackSnapshots.filter((candidate) => candidate.id !== snapshotId),
          error: null,
          ...(() => {
            const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, restoredData);
            return {
              editedCells,
              hasPendingChanges: resolveHasPendingQuickFillChanges(
                state.baselineQuickFillData,
                restoredData,
                editedCells,
                true
              ),
            };
          })(),
        }));

        return true;
      },

      discardRollbackSnapshot: (snapshotId) => {
        set((state) => ({
          rollbackSnapshots: state.rollbackSnapshots.filter((candidate) => candidate.id !== snapshotId),
        }));
      },

      captureSessionUndoSnapshot: (label, sessionId) => {
        const state = get();
        const normalizedSessionId = sessionId.trim();
        const data = Array.isArray(state.quickFillData) ? state.quickFillData : [];

        if (!normalizedSessionId || data.length === 0) {
          return null;
        }

        const existing = state.sessionUndoSnapshots.find(
          (candidate) => candidate.sessionId === normalizedSessionId
        );
        if (existing) {
          return existing.id;
        }

        const snapshotId = `quickfill-session-snapshot-${Date.now()}-${Math.round(Math.random() * 1000)}`;
        const snapshot: QuickFillSessionUndoSnapshot = {
          id: snapshotId,
          sessionId: normalizedSessionId,
          label: label.trim() || 'Quick Fill session undo snapshot',
          createdAt: new Date().toISOString(),
          rowIdentity: buildSessionUndoRowIdentity(data),
          properties: cloneQuickFillProperties(Array.isArray(state.properties) ? state.properties : []),
          extraProperties: cloneQuickFillProperties(Array.isArray(state.extraProperties) ? state.extraProperties : []),
          quickFillData: cloneQuickFillData(data),
        };

        set((current) => ({
          sessionUndoSnapshots: [
            snapshot,
            ...current.sessionUndoSnapshots.filter(
              (candidate) => candidate.sessionId !== normalizedSessionId
            ),
          ].slice(0, 5),
        }));

        return snapshotId;
      },

      restoreSessionUndoSnapshot: (snapshotId) => {
        const state = get();
        const snapshot = state.sessionUndoSnapshots.find((candidate) => candidate.id === snapshotId);

        if (!snapshot || !isQuickFillSessionUndoSnapshotCompatible(snapshot, state.quickFillData)) {
          return false;
        }

        const restoredData = cloneQuickFillData(snapshot.quickFillData);

        set((current) => ({
          properties: cloneQuickFillProperties(snapshot.properties),
          extraProperties: cloneQuickFillProperties(snapshot.extraProperties),
          quickFillData: restoredData,
          error: null,
          ...(() => {
            const editedCells = buildEditedCellsFromBaseline(current.baselineQuickFillData, restoredData);
            return {
              editedCells,
              hasPendingChanges: resolveHasPendingQuickFillChanges(
                current.baselineQuickFillData,
                restoredData,
                editedCells,
                true
              ),
            };
          })(),
        }));

        return true;
      },

      clearSessionUndoSnapshots: (snapshotIds) => {
        const idsToClear = new Set(snapshotIds);
        if (idsToClear.size === 0) {
          return;
        }

        set((state) => ({
          sessionUndoSnapshots: state.sessionUndoSnapshots.filter(
            (candidate) => !idsToClear.has(candidate.id)
          ),
        }));
      },

      // Apply actions
      applyQuickFill: async (_itemNames) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call delay
          await new Promise((resolve) => setTimeout(resolve, 500));

          // TODO: Call the Revit API to apply checked property values to selected items
          set({ isLoading: false, selectedItems: [] });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to apply quick fill';
          set({ error: errorMessage, isLoading: false });
        }
      },

      // Auto-fill actions
      autoFillRows: (indices) => {
        const { properties } = get();
        const props = Array.isArray(properties) ? properties : [];

        // Get checked property labels
        const checkedLabels = props
          .filter((p) => p && p.checked)
          .map((p) => p.label);

        return get().autoFillRowsWithLabels(indices, checkedLabels);
      },

      autoFillRowsWithLabels: (indices, labels, mode = 'fillEmpty') => {
        const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const { quickFillData } = get();
        const data = Array.isArray(quickFillData) ? quickFillData : [];
        const allowedLabels = new Set(
          (Array.isArray(labels) ? labels : []).filter(
            (label): label is string => typeof label === 'string' && label.trim() !== ''
          )
        );
        const rowIndices = sortUniqueRowIndices(indices);

        webLog.info(AutoFillLogSource, stringifyAutoFillStoreLog('store-apply-start', {
          mode,
          dataRows: data.length,
          requestedRows: rowIndices.length,
          labels: Array.from(allowedLabels),
          sampleRows: rowIndices
            .slice(0, AutoFillLogSampleSize)
            .map((rowIndex) => summarizeAutoFillStoreRow(data[rowIndex], rowIndex)),
        }));

        const { updatedData, filledCount: filled, diagnostics, results } = computeAutoFillRows(
          data,
          rowIndices,
          allowedLabels,
          mode
        );

        // Update store
        set((state) => {
          const editedCells = buildEditedCellsFromBaseline(state.baselineQuickFillData, updatedData);
          const hasPendingChanges = resolveHasPendingQuickFillChanges(
            state.baselineQuickFillData,
            updatedData,
            editedCells,
            filled > 0
          );
          return {
            quickFillData: updatedData,
            editedCells,
            hasPendingChanges,
          };
        });

        const pendingState = get();
        logQuickFillAttention('store-auto-fill-pending-state', {
          mode,
          filled,
          total: rowIndices.length,
          matchedRows: results.size - diagnostics.noConfidenceRows,
          touchedTargetCells: diagnostics.touchedTargetCells,
          editedCellCount: getEditedCellCount(pendingState.editedCells),
          hasPendingChanges: pendingState.hasPendingChanges,
          baselineRows: pendingState.baselineQuickFillData.length,
          currentRows: pendingState.quickFillData.length,
          labels: Array.from(allowedLabels),
          sampleEditedKeys: Object.keys(pendingState.editedCells).slice(0, 8),
        });

        const matchedRows = results.size - diagnostics.noConfidenceRows;
        const finishPayload = {
          mode,
          elapsedMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt)),
          dataRows: data.length,
          requestedRows: rowIndices.length,
          matchedRows,
          filled,
          labels: Array.from(allowedLabels),
          diagnostics,
        };
        if (filled === 0 && matchedRows > 0 && diagnostics.touchedTargetCells === 0) {
          webLog.info(AutoFillLogSource, stringifyAutoFillStoreLog('store-apply-no-changes', finishPayload));
        } else if (filled === 0 && rowIndices.length > 0) {
          webLog.warn(AutoFillLogSource, stringifyAutoFillStoreLog('store-apply-zero-filled', finishPayload));
        } else {
          webLog.info(AutoFillLogSource, stringifyAutoFillStoreLog('store-apply-complete', finishPayload));
        }

        return { filled, total: rowIndices.length, results };
      },

      autoFillAllRows: () => {
        const { quickFillData } = get();
        const data = Array.isArray(quickFillData) ? quickFillData : [];
        const allIndices = data.map((_, index) => index);
        return get().autoFillRows(allIndices);
      },

      autoFillSelectedRows: () => {
        const { quickFillData, selectedItems } = get();
        const data = Array.isArray(quickFillData) ? quickFillData : [];
        const selected = Array.isArray(selectedItems) ? selectedItems : [];

        // Find indices of selected items by name
        const indices: number[] = [];
        data.forEach((item, index) => {
          if (item && selected.includes(item.Name)) {
            indices.push(index);
          }
        });

        return get().autoFillRows(indices);
      },

      // Reset
      reset: () => {
        set({
          ...initialState,
          properties: createDefaultQuickFillProperties(),
        });
      },
    }),
    {
      name: 'pipeex-quickfill-store-v2', // Changed name to force fresh start
      version: 9, // Bumped version - session undo snapshots are session-scoped and no longer persisted
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Persist properties configuration and quick fill data.
        // sessionUndoSnapshots stay in memory: each snapshot embeds a full data
        // clone, and persist serializes the partialized state on every set(),
        // so persisting them multiplied the per-keystroke JSON cost by ~6x.
        properties: state.properties,
        extraProperties: state.extraProperties,
        quickFillData: state.quickFillData,
      }),
      // Migration to handle old/corrupted data
      migrate: (persistedState: any, version: number) => {
        // If coming from old version or corrupted, return fresh state
        if (version < 2 || !persistedState) {
          return {
            properties: [],
            extraProperties: [],
            quickFillData: [],
          };
        }

        // Map of old parameter names to new ones
        const parameterNameMigrations: Record<string, string> = {
          'PiX_01_SKEY': '#PCF_SKEY',
          'PiX_02_OBJECT_TYPE': '#PCF_OBJECT_TYPE',
          'PiP_PPG_SPEC': 'PiX_PPG_Spec',
          'PiX_PPG_INSULATION_TYPE': 'PiX_GN_2_InsulationSpec',
          'PiX_PPG_INSULATION_THICKNESS': 'PiX_GN_2_InsulationThickness',
        };

        // Clear all options from properties and fix revitParameter names
        const rawProperties = Array.isArray(persistedState.properties) ? persistedState.properties : [];
        const cleanedProperties = rawProperties.map((prop: any) => {
          if (!prop) return null;
          // Migrate old revitParameter names to new ones
          const migratedRevitParam = prop.revitParameter && parameterNameMigrations[prop.revitParameter]
            ? parameterNameMigrations[prop.revitParameter]
            : prop.revitParameter;
          return {
            ...prop,
            revitParameter: migratedRevitParam,
            options: [], // Clear options - will be populated from shared parameters/workflow
          };
        })
          .map(sanitizeQuickFillProperty)
          .filter((prop: any) => !shouldExcludeQuickFillProperty(prop));

        return {
          properties: cleanedProperties,
          extraProperties: (Array.isArray(persistedState.extraProperties) ? persistedState.extraProperties : [])
            .map(sanitizeQuickFillProperty)
            .filter((prop: any) => !shouldExcludeQuickFillProperty(prop)),
          quickFillData: Array.isArray(persistedState.quickFillData) ? persistedState.quickFillData : [],
        };
      },
      onRehydrateStorage: () => () => {},
    }
  )
);

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

export const useQuickFillProperties = () =>
  useQuickFillStore((state) => Array.isArray(state.properties) ? state.properties : initialState.properties);

export const useCheckedProperties = () =>
  useQuickFillStore(
    useShallow((state) => {
      const props = Array.isArray(state.properties) ? state.properties : [];
      return props.filter((p) => p && p.checked);
    })
  );

export const useQuickFillData = () =>
  useQuickFillStore((state) => Array.isArray(state.quickFillData) ? state.quickFillData : initialState.quickFillData);

export const useQuickFillEditedCells = () =>
  useQuickFillStore((state) => state.editedCells);

export const useQuickFillFilters = () =>
  useQuickFillStore((state) => state.filters);

export const useFilteredQuickFillData = () => {
  const quickFillData = useQuickFillStore((state) => state.quickFillData);
  const filters = useQuickFillStore((state) => state.filters);

  return useMemo(() => {
    // Ensure quickFillData is an array
    const data = Array.isArray(quickFillData) ? quickFillData : [];

    if (!filters || Object.values(filters).every((v) => !v)) {
      return data;
    }

    return data.filter((item) => {
      if (!item) return false;
      return Object.entries(filters).every(([field, value]) => {
        if (!value) return true;
        const itemValue = item[field as keyof QuickFillItem];
        return itemValue
          ? String(itemValue).toLowerCase().includes(value.toLowerCase())
          : false;
      });
    });
  }, [filters, quickFillData]);
};

export const useQuickFillSelectedItems = () =>
  useQuickFillStore((state) => Array.isArray(state.selectedItems) ? state.selectedItems : initialState.selectedItems);

export const useQuickFillLoading = () =>
  useQuickFillStore((state) => state.isLoading);

export const useQuickFillError = () =>
  useQuickFillStore((state) => state.error);

export const useQuickFillRollbackSnapshots = () =>
  useQuickFillStore((state) => state.rollbackSnapshots);

export const useIsAllSelected = () => {
  const filteredData = useFilteredQuickFillData();
  const selectedItems = useQuickFillStore((state) => state.selectedItems);
  return useMemo(() => {
    const items = Array.isArray(selectedItems) ? selectedItems : [];
    const itemNames = new Set(items);
    return (
      filteredData.length > 0 &&
      filteredData.every((item) => item && itemNames.has(item.Name))
    );
  }, [filteredData, selectedItems]);
};

export const usePropertyById = (id: string) =>
  useQuickFillStore((state) => {
    const props = Array.isArray(state.properties) ? state.properties : [];
    return props.find((p) => p && p.id === id);
  });
